import React, { useState, useEffect } from 'react';
import { X, Search, Users2 } from 'lucide-react';
import { supabase } from '../utils/supabase';

interface Contact {
  id: number;
  name: string;
  company: string;
  role: string;
  email: string;
  phone: string;
}

interface Double {
  id: string;
  name: string;
  status: 'active' | 'training' | 'inactive';
}

interface CreateMeetingModalProps {
  onClose: () => void;
  onSubmit: (meeting: {
    title: string;
    contactId: number;
    doubleId: string;
    type: 'inbound' | 'outbound';
    date: string;
    time: string;
  }) => void;
  contacts: Contact[];
  initialDate?: string;
}

const CreateMeetingModal = ({ onClose, onSubmit, contacts, initialDate }: CreateMeetingModalProps) => {
  const [formData, setFormData] = useState({
    title: '',
    contactId: 0,
    doubleId: '',
    type: 'outbound' as 'inbound' | 'outbound',
    date: initialDate || '',
    time: '',
    timezone: 'Eastern' as 'Eastern' | 'Central' | 'Mountain' | 'Pacific'
  });

  const [doubles, setDoubles] = useState<Double[]>([]);
  const [loadingDoubles, setLoadingDoubles] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactsList, setShowContactsList] = useState(false);
  
  const timezones = [
    { value: 'Eastern', label: 'ET' },
    { value: 'Central', label: 'CT' },
    { value: 'Mountain', label: 'MT' },
    { value: 'Pacific', label: 'PT' }
  ];

  useEffect(() => {
    const fetchDoubles = async () => {
      try {
        const { data, error } = await supabase
          .from('doubles')
          .select('id, name, status')
          .eq('status', 'active')
          .order('name');

        if (error) throw error;
        setDoubles(data || []);
      } catch (err) {
        console.error('Error fetching doubles:', err);
      } finally {
        setLoadingDoubles(false);
      }
    };

    fetchDoubles();
  }, []);

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.company.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedContact = contacts.find(c => c.id === formData.contactId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleContactSelect = (contact: Contact) => {
    setFormData(prev => ({ ...prev, contactId: contact.id }));
    setShowContactsList(false);
  };

  const handleDoubleSelect = (doubleId: string) => {
    setFormData(prev => ({ ...prev, doubleId }));
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-md rounded-xl shadow-retro border-2 border-dark"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Create Meeting</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Title
            </label>
            <input
              type="text"
              id="title"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Double
            </label>
            <div className="grid grid-cols-2 gap-3">
              {loadingDoubles ? (
                <div className="col-span-2 text-center py-4">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                </div>
              ) : doubles.length === 0 ? (
                <div className="col-span-2 text-center py-4 text-gray-500">
                  No active doubles available
                </div>
              ) : doubles.map((double) => (
                <button
                  key={double.id}
                  type="button"
                  onClick={() => handleDoubleSelect(double.id)}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    formData.doubleId === double.id
                      ? 'bg-primary/10 border-primary shadow-none -translate-y-1'
                      : 'bg-white border-dark shadow-retro hover:shadow-none hover:-translate-y-1'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-medium">{double.name[0]}</span>
                  </div>
                  <span className="font-medium">{double.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="relative">
            <label htmlFor="contact" className="block text-sm font-medium text-gray-700 mb-1">
              Contact
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                id="contact"
                required
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowContactsList(true);
                }}
                onFocus={() => setShowContactsList(true)}
                placeholder="Search contacts..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            
            {showContactsList && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredContacts.map(contact => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleContactSelect(contact)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-medium text-sm">
                        {contact.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium">{contact.name}</div>
                      <div className="text-sm text-gray-600">{contact.company}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {selectedContact && (
              <div className="mt-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-medium">
                    {selectedContact.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <div className="font-medium">{selectedContact.name}</div>
                  <div className="text-sm text-gray-600">{selectedContact.company}</div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Call Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  value="outbound"
                  checked={formData.type === 'outbound'}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'outbound' | 'inbound' }))}
                  className="text-primary focus:ring-primary"
                />
                <span>Outbound</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  value="inbound"
                  checked={formData.type === 'inbound'}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'outbound' | 'inbound' }))}
                  className="text-primary focus:ring-primary"
                />
                <span>Inbound</span>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <div className="grid grid-cols-4 gap-2">
              {timezones.map((timezone) => (
                <button
                  key={timezone.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, timezone: timezone.value as typeof formData.timezone }))}
                  className={`py-2 px-4 rounded-lg text-center transition-all border-2 ${
                    formData.timezone === timezone.value
                      ? 'bg-primary text-white border-primary shadow-none -translate-y-1'
                      : 'bg-white border-dark hover:bg-gray-50 shadow-retro hover:shadow-none hover:-translate-y-0.5'
                  }`}
                >
                  {timezone.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                id="date"
                required
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <input
                type="time"
                id="time"
                required
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.doubleId || !formData.contactId || !formData.date || !formData.time}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Create Meeting
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMeetingModal;